import { BasicValue } from '@cutr/constants/windows/windows-configurations.ts';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { onImgError } from '@/blocks/materialRowUtils';
import { Modal } from '@/blocks/Modal';
import { Button } from '@/primitives/Button';
import { Edit, Icon } from '@/primitives/Icons';
import { useToggle } from '@/utils/hooks';

import { unboxLabel } from './configurations/Configurations';
import { useConfigurations } from './configurations/ConfigurationsContext';
import { ConfigurationButton } from './configurations/inputs/ConfigurationButton';
import styles from './GridSelectModal.module.css';

type GridSelectModalProps = {
  configurationKey:
    | 'windowSills'
    | 'windowSillDecorativeProfile'
    | 'weatherRail';
  dispatchAction:
    | 'SET_WINDOW_SILLS'
    | 'SET_WINDOW_SILL_DECORATIVE_PROFILE'
    | 'SET_WEATHER_RAIL';
  options: { config: BasicValue; imagePath: string }[];
  buttonId?: string;
};

export const GridSelectModal = ({
  configurationKey,
  dispatchAction,
  options,
  buttonId,
}: GridSelectModalProps) => {
  const { t } = useTranslation();
  const [isOpen, toggleModal] = useToggle(false);

  const { configurations } = useConfigurations();
  const label = unboxLabel(configurations[configurationKey]?.label);
  return (
    <>
      <div>
        <ConfigurationButton
          label={label}
          onClick={toggleModal}
          icon={<Icon icon={<Edit />} size={2.5} />}
          id={buttonId}
        />
      </div>
      {isOpen && (
        <Modal
          title={t(`windows.configurations.${configurationKey}.modal.title`)}
          isOpen={isOpen}
          handleClose={() => {
            toggleModal();
          }}
        >
          <ModalInner
            toggleModal={toggleModal}
            options={options}
            dispatchAction={dispatchAction}
            selectedValue={configurations[configurationKey]?.value || null}
            configurationKey={configurationKey}
          />
        </Modal>
      )}
    </>
  );
};

type ModalInnerProps = {
  toggleModal: () => void;
  options: GridSelectModalProps['options'];
  dispatchAction: GridSelectModalProps['dispatchAction'];
  selectedValue: string | null;
  configurationKey: GridSelectModalProps['configurationKey'];
  columnsCount?: number;
};

const ModalInner = ({
  toggleModal,
  options,
  dispatchAction,
  selectedValue,
  configurationKey,
  columnsCount = 3,
}: ModalInnerProps) => {
  const { t } = useTranslation();
  const { dispatch } = useConfigurations();
  const [selected, setSelected] = React.useState<string | null>(
    selectedValue || null
  );

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = options.find((option) => option.config.value === selected)!;

    dispatch({
      type: dispatchAction,
      value: value.config,
    });
    toggleModal();
  };

  const handleClick = (selectedOption: string) => {
    setSelected(selectedOption);
  };

  return (
    <>
      <p>{t(`windows.configurations.${configurationKey}.modal.description`)}</p>

      <form onSubmit={onSubmit} className="stack">
        <div
          className={styles.modalLayout}
          style={{ gridTemplateColumns: `repeat(${columnsCount}, 1fr)` }}
        >
          {options.map((option: GridSelectModalProps['options'][number]) => {
            const optionValue = option.config;

            return (
              <GridItem
                key={optionValue.value}
                option={option}
                onClick={handleClick}
                selected={selected === optionValue.value}
              />
            );
          })}
        </div>
        <Button type="submit">{t('common.cta.save')}</Button>
      </form>
    </>
  );
};

type GridItemProps = {
  option: GridSelectModalProps['options'][number];
  onClick: (selected: string) => void;
  selected: boolean;
};

const GridItem = ({ option, onClick, selected }: GridItemProps) => {
  const optionValue = option.config;
  const imagePath = option.imagePath;
  const [loaded, setLoaded] = React.useState(false);

  return (
    <div
      className={cn(styles.gridItem, selected && styles.selected)}
      onClick={() => onClick(optionValue.value)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick(optionValue.value);
        }
      }}
      aria-label={unboxLabel(optionValue.label)}
    >
      <div className={cn(styles.imageWrapper, !loaded && styles.skeleton)}>
        <img
          src={imagePath}
          alt={unboxLabel(optionValue.label)}
          onLoad={() => setLoaded(true)}
          onError={(e) => {
            setLoaded(true);
            onImgError(e);
          }}
          data-loaded={loaded}
        />
      </div>
      <strong className={styles.gridItemLabel}>
        {unboxLabel(optionValue.label)}
      </strong>
    </div>
  );
};
